import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Card,
  Heading,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { FHIR_MIRROR_ENTITIES, type FhirMirrorEntity } from '@eluve/fhir-utils';

/**
 * Medplum management for a single tenant — today just the eluve→FHIR mirror
 * backfills, but this is where the rest of the per-tenant Medplum actions land.
 *
 * The mirror is normally kept current by a DB trigger, so the backfills exist
 * for the cases the trigger can't cover: tenants whose rows predate the mirror,
 * and re-syncs after a mapping change. Enqueue-only — jobs carry the same key
 * the trigger uses, so pressing a button twice, or pressing it while edits are
 * landing, still leaves one job per row.
 */

type MedplumProject = {
  id: string;
  name: string;
  tenantId: string | null;
};

/** One button per mirrored entity, so a new entity shows up here for free. */
const ENTITY_LABELS: Record<FhirMirrorEntity, string> = {
  location: 'Locations',
  practitioner: 'Practitioners',
};

const BackfillRow: React.FC<{
  tenantId: string;
  tenantName: string;
  entity: FhirMirrorEntity;
}> = ({ tenantId, tenantName, entity }) => {
  const apiClient = useApiClient();
  const toast = useToast();
  const [pending, setPending] = useState(false);
  const label = ENTITY_LABELS[entity];

  const backfill = async () => {
    setPending(true);
    try {
      const result = await apiClient.medplumAdmin.backfillMirror({
        params: { tenantId },
        body: { entity },
      });

      if (result.status === 200) {
        toast.success(
          `Queued ${result.body.enqueued} ${label.toLowerCase()} for ${tenantName}.`,
        );
      } else {
        toast.error("Couldn't queue sync.");
      }
    } catch {
      toast.error('Failed to reach API');
    } finally {
      setPending(false);
    }
  };

  return (
    <tr className="border-b last:border-b-0">
      <td className="py-2">{label}</td>
      <td className="py-2">
        <Stack direction="horizontal" gap={2} fullWidth={false} justify="end">
          <Button
            variant="outline"
            size="sm"
            disabled={pending}
            onClick={() => void backfill()}
            data-testid={`backfill-${entity}`}
          >
            {pending ? 'Queueing…' : `Sync ${label}`}
          </Button>
        </Stack>
      </td>
    </tr>
  );
};

const AccessPolicySection: React.FC<{
  tenantId: string;
  tenantName: string;
}> = ({ tenantId, tenantName }) => {
  const apiClient = useApiClient();
  const toast = useToast();
  const [pending, setPending] = useState(false);

  const resync = async () => {
    setPending(true);
    try {
      const result = await apiClient.medplumAdmin.syncAccessPolicies({
        params: { tenantId },
        body: {},
      });

      if (result.status === 200) {
        toast.success(
          `Re-synced ${result.body.synced} member policies for ${tenantName}` +
            (result.body.skipped > 0
              ? ` (${result.body.skipped} not in Medplum yet).`
              : '.'),
        );
      } else {
        toast.error("Couldn't re-sync policies.");
      }
    } catch {
      toast.error('Failed to reach API');
    } finally {
      setPending(false);
    }
  };

  return (
    <Card className="p-4">
      <Heading size="2xl" weight="semibold" className="mb-2">
        Access policies
      </Heading>
      <Text size="md" color="tertiary" className="mb-4">
        Re-applies each member's Medplum AccessPolicy from their current eluve
        role. Policies otherwise only sync on a member's next login — use this
        after role changes or a policy definition change. Safe to re-run.
      </Text>
      <Stack direction="horizontal" gap={2} fullWidth={false}>
        <Button
          variant="outline"
          size="sm"
          disabled={pending}
          onClick={() => void resync()}
          data-testid="resync-access-policies"
        >
          {pending ? 'Re-syncing…' : 'Re-sync policies'}
        </Button>
      </Stack>
    </Card>
  );
};

export const MedplumTenantPage: React.FC = () => {
  const { tenantId } = useParams() as { tenantId: string };
  const apiClient = useApiClient();
  const [project, setProject] = useState<MedplumProject | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const result = await apiClient.medplumAdmin.diagnostics();
        setProject(
          result.status === 200
            ? (result.body.projects.find((p) => p.tenantId === tenantId) ??
                null)
            : null,
        );
      } catch {
        setProject(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [apiClient, tenantId]);

  if (loading) {
    return <Text size="md">Loading tenant…</Text>;
  }

  if (!project) {
    return (
      <Stack gap={4}>
        <Text size="md">No Medplum project found for this tenant.</Text>
        <Link
          to="/admin/medplum"
          className="text-text-accent-secondary underline"
        >
          Back to Medplum
        </Link>
      </Stack>
    );
  }

  return (
    <div className="space-y-6">
      <Stack gap={2}>
        <Link to="/admin/medplum" className="text-sm text-text-tertiary">
          ← Medplum
        </Link>
        <Heading size="4xl" weight="bold">
          {project.name}
        </Heading>
        <Text size="sm" color="tertiary" className="font-mono">
          Project {project.id} · Tenant {tenantId}
        </Text>
      </Stack>

      <Card className="p-4">
        <Heading size="2xl" weight="semibold" className="mb-2">
          FHIR Mirror
        </Heading>
        <Text size="md" color="tertiary" className="mb-4">
          Queues a re-sync of this tenant's eluve rows into their FHIR mirror.
          Ongoing changes sync automatically; use this for tenants whose data
          predates the mirror, or after a mapping change. Safe to re-run.
        </Text>
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b text-text-tertiary">
              <th className="pb-2 font-medium">Entity</th>
              <th className="pb-2 text-right font-medium">Backfill</th>
            </tr>
          </thead>
          <tbody>
            {FHIR_MIRROR_ENTITIES.map((entity) => (
              <BackfillRow
                key={entity}
                entity={entity}
                tenantId={tenantId}
                tenantName={project.name}
              />
            ))}
          </tbody>
        </table>
      </Card>

      <AccessPolicySection tenantId={tenantId} tenantName={project.name} />
    </div>
  );
};
